import { clsx } from 'clsx';
import React from 'react';
import { AddWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/AddWorkflowPopup/AddWorkflowPopup';
import { API_VERSION } from 'src/constants/api-routes';
import { Button } from 'src/components/ui/button';
import { AddIcon } from 'src/assets/img/add-icon';
import { useConfigurationContext } from 'src/provider/ConfigurationProvider';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import { getApiVersion } from 'src/utils/apiVersion';

interface WorkflowsFormBlockProps {
  label: React.ReactNode;
  children: React.ReactNode;
  compact?: boolean;
  // The block's form fields; it renders only when at least one is enabled (truthy).
  fields: unknown[];
  readonly?: boolean;
  isCustom?: boolean;
  onWorkflowAdd?: (workflow: Workflow) => void;
  onEditClick?: () => void;
}

export const WorkflowsFormBlock = ({
  label,
  children,
  compact,
  fields,
  readonly = true,
  isCustom = true,
  onWorkflowAdd = () => {},
  onEditClick = () => {},
}: WorkflowsFormBlockProps) => {
  const { setIsEditWorkflowMode, setSelectedWorkflow } = useConfigurationContext();
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;

  const addV2Workflow = () => {
    onEditClick();
    setIsEditWorkflowMode(true);
    setSelectedWorkflow(null);
  };

  return fields.filter(Boolean).length ? (
    <div className={clsx('flex gap-2.5 rounded-sm-5', compact ? 'flex-col' : 'flex-row')}>
      <div
        className={clsx(
          'flex flex-row text-[#7A6BE5] font-bold text-sm justify-between px-0 min-h-[34px]',
          !compact ? 'min-w-[200px] items-start py-3.5' : 'items-center',
        )}
      >
        <div>
          <p>{label}</p>
        </div>
        {isCustom &&
          compact &&
          (isV1 ? (
            <AddWorkflowPopup onEditClick={onEditClick} onSubmit={onWorkflowAdd} />
          ) : (
            <Button
              variant={'outline'}
              className="flex flex-row items-center gap-2 text-ebony_opacity_80 font-medium"
              onClick={addV2Workflow}
            >
              <span>Add Workflow</span>
              <span>
                <AddIcon />
              </span>
            </Button>
          ))}
      </div>
      <div
        className={clsx(
          'flex w-full gap-2.5',
          compact ? 'flex-row items-center' : 'flex-col items-start',
        )}
      >
        <div className={'flex flex-col w-full'}>{children}</div>
        {isCustom &&
          !compact &&
          !readonly &&
          (isV1 ? (
            <AddWorkflowPopup
              trigger={
                <div className={'pl-7 py-1.5'}>
                  <p className={'text-[#7A6BE5] text-xs font-medium hover:underline'}>
                    Add workflows +
                  </p>
                </div>
              }
              onSubmit={onWorkflowAdd}
            />
          ) : (
            <Button
              variant={'outline'}
              className="flex flex-row items-center gap-2 text-ebony_opacity_80 font-medium"
              onClick={addV2Workflow}
            >
              <span>Add Workflow</span>
              <span>
                <AddIcon />
              </span>
            </Button>
          ))}
      </div>
    </div>
  ) : null;
};
